import { KEY } from 'constants/local_storage_key.constant';
import { CommonEquipmentService, dayjs, Equipment, usePersistantState, UUID } from '@umanforge/common';
import { Planning } from 'interfaces/planning.interface';
import { ModifyTask, PostTask, TaskFull } from 'interfaces/task.interface';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlanningService } from 'services/planning.service';
import { TaskService } from 'services/task.service';
import { RootState } from '@umanforge/common';
import { SectorSettings } from 'interfaces/setting.interface';
import { SettingService } from 'services/setting.service';
import { AxiosError, AxiosResponse } from 'axios';
import { GlobalService } from 'services/global.service';
import { TaskEquipmentService } from 'services/task_equipment.service';
import { TaskEquipment } from 'interfaces/task_equipment.interface';
import useTaskActionImages from './taskActionImages.hook';
import { TaskAction } from 'interfaces/taskAction.interface';


function usePlanningView() {
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );
  const [ , setClientId ] = usePersistantState< UUID | undefined >( KEY.SELECTED_CLIENT_ID, undefined );


  const [ currentDayISO, setCurrentDayISO ] = usePersistantState< string | undefined >( KEY.SELECTED_DAY, undefined );
  const [ currentDay, setCurrentDay ] = useState<dayjs.Dayjs>( dayjs( currentDayISO ).startOf( 'week' ) );

  const [ selectedViewPersistent, setSelectedViewPersistent ] = usePersistantState< string | undefined >( KEY.SELECTED_VIEW, 'week' );
  const [ isDayView, setIsDayView ] = useState( selectedViewPersistent == 'day' );

  const [ planning, setPlanning ] = useState<Planning>(  );
  const [ tasks, setTasks ] = useState<TaskFull[]>( [] );
  const [ settings, setSettings ] = useState<SectorSettings>( );
  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ equipments, setEquipments ] = useState<Equipment[]>( [] );
  const [ equipmentsTasks, setEquipmentsTasks ] = useState<TaskEquipment[]>( [] );
  const [ taskActions, setTaskAction ] = useState<TaskAction[]>( [] );

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ update, setUpdate ] = useState( 0 );

  const taskActionImages = useTaskActionImages( taskActions );

  const dates = useMemo( () => {
    return {
      dateStart: currentDay.startOf( 'day' ).toISOString(),
      dateEnd: isDayView ?
        currentDay.endOf( 'day' ).toISOString() :
        currentDay.add( 6, 'day' ).endOf( 'day' ).toISOString()
    };
  }, [ currentDay, isDayView ] );

  //--------------- useEffect ---------------\\
  // Chargement initial
  useEffect( () => {
    init();
  }, [] );

  useEffect( () => {
    setIsDayView( selectedViewPersistent == 'day' );
  }, [ selectedViewPersistent ] );

  // Récupération du planning
  useEffect( () => {
    if ( !isFirstRender ) {
      refreshPlanning( selectedSectorId );
    }
    else {
      init();
    }
  }, [ selectedSectorId, token ] );

  // Récupération des tasks
  useEffect( () => {
    if ( !isFirstRender ) {
      refreshTasks( planning?.id );
    }
  }, [ planning, dates, isFirstRender ] );

  useEffect( () => {
    if ( currentDayISO != null ) {
      setCurrentDay( dayjs( currentDayISO ) );
    }
  }, [ currentDayISO ] );


  //--------------- Functions ---------------\\
  const init = async () => {
    if ( selectedSectorId ) {
      const planningId = await refreshPlanning( selectedSectorId );
      await refreshTasks( planningId );
      setIsFirstRender( false );
    }
  };

  const handleSetCurrentDay = ( day: dayjs.Dayjs ) => {
    if ( !isFirstRender ) {
      setCurrentDayISO( day.toISOString() );
    }
  };

  const onFetchPlanningError = ( error: AxiosError<unknown> ) => {
    console.error( 'Error fetching planning', error );
    if ( error.status == 403 ) {
      const client = me?.clients[ 0 ];
      if ( client ) {
        setClientId( client.id );
      }
      return undefined;
    }
  };

  const refreshPlanning = async ( sectorId: string | undefined ): Promise<string | undefined> => {
    if ( sectorId ) {
      const resPlanning = await PlanningService.getPlanningBySectorId( sectorId, token, dispatch, onFetchPlanningError );
      setPlanning( resPlanning );
      setSettings( await SettingService.getSectorSettingsBySector( sectorId, token, dispatch ) );
      return resPlanning.id;
    }
    else {
      setPlanning( undefined );
      setSettings( undefined );
    }
    return undefined;
  };

  const refreshTasks = async ( planningId: string | undefined ) => {
    if ( planningId ) {
      const [ fetchedTasks, fetchedEquipments ] = await Promise.all( [
        TaskService.getTasksByPlanningIdAndDate( planningId, dates, token, dispatch ),
        CommonEquipmentService.getAllowedEquipmentsBySectorsId( [ selectedSectorId ?? '' ], token, dispatch )
      ] );
      setTasks( fetchedTasks );
      setEquipments( fetchedEquipments );
      setTaskAction( [ ...new Set( fetchedTasks.map( task => { return task.taskAction; } ) ) ] );

      if ( fetchedTasks.length > 0 ) {
        setEquipmentsTasks( await TaskEquipmentService.getTaskEquipmentByTasksId( fetchedTasks.map( task => { return task.id; } ), token, dispatch  ) );
      }
      setUpdate( update + 1 );
    }
    else {
      setTasks( [] );
      setEquipments( [] );
    }
  };

  // update functions
  const onTaskCreate = async ( task: PostTask ): Promise<string> => {
    const taskPost: PostTask = {
      idPlanning: task.idPlanning,
      idLocation: task.idLocation,
      idTaskAction: task.idTaskAction,
      status: task.status,
      debord: task.debord,
      nbSmallPackage: task.nbSmallPackage,
      nbBigPackage: task.nbBigPackage,
      dateStart: task.dateStart,
      dateEnd: task.dateEnd,
      idRecurrence: task.idRecurrence,
    };

    const taskId = ( await TaskService.postTask( taskPost, token, dispatch ) ).id;
    await refreshTasks( planning?.id );
    return taskId;
  };

  const onTaskUpdate = async ( task: ModifyTask, refreshTask: boolean = true ): Promise<boolean> => {
    const taskModify: ModifyTask = {
      idLocation: task.idLocation,
      idTaskAction: task.idTaskAction,
      status: task.status,
      debord: task.debord,
      nbSmallPackage: task.nbSmallPackage,
      nbBigPackage: task.nbBigPackage,
      dateStart: task.dateStart,
      dateEnd: task.dateEnd,
      idRecurrence: task.idRecurrence,
      id: task.id
    };

    let passed = false;
    await TaskService.putTask( task.id, taskModify, token, dispatch ).then( () => {
      if ( refreshTask ) {
        refreshTasks( planning?.id );
      }
      passed = true;
    } );

    return passed;
  };

  const onTaskDelete = async ( taskId: string ) => {
    await TaskService.deleteTask( taskId, token, dispatch );
    await refreshTasks( planning?.id );
  };


  const onSectorsChange = ( sectorId: string | undefined ) => {
    setSelectedSectorId( sectorId );
  };

  const onViewChange = ( checked: boolean ) => {
    if ( !isFirstRender ) {
      setIsDayView( checked );
      setSelectedViewPersistent( checked ? 'day' : 'week' );
      if ( checked ) {
        setCurrentDayISO( dayjs().startOf( 'day' ).toISOString() );
      }
      else {
        setCurrentDayISO( currentDay.startOf( 'week' ).toISOString() );
      }
    }
  };

  const onAxiosWarning = ( warn : AxiosResponse ) => {
    GlobalService.onAxiosWarning( warn );
  };

  const onAxiosError = ( err : AxiosError ) => {
    GlobalService.onAxiosError( err, dispatch );
  };

  return {
    currentDay, setCurrentDay: handleSetCurrentDay,
    planning,
    tasks,
    settings,
    selectedSectorId, setSelectedSectorId,
    isDayView,
    equipments,
    equipmentsTasks,
    onSectorsChange,
    onAxiosWarning,
    onAxiosError,
    onTaskCreate,
    onTaskUpdate,
    onTaskDelete,
    onViewChange,
    refreshTasks,
    taskActionImages,
    update
  };
}

export default usePlanningView;